import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MessageCircleQuestion } from 'lucide-react';

const faqData = [
  {
    category: "Le Concept & L'Utilisation de base",
    questions: [
      {
        q: "Comment fonctionne le système de swipe ?",
        a: "C'est très simple ! Swipez vers la droite pour \"liker\" un vêtement, et vers la gauche pour passer. Il n'y a pas de super like, le but est de faire des choix simples et rapides."
      },
      {
        q: "J'ai passé un vêtement par erreur, puis-je revenir en arrière ?",
        a: "Oui ! Rendez-vous dans l'onglet \"Historique\". Vous y retrouverez les derniers articles vus. Vous pourrez alors liker un article passé par erreur, ou retirer un like donné trop vite."
      },
      {
        q: "D'où viennent les marques et les vêtements proposés ?",
        a: "En cliquant sur une marque, vous serez redirigé vers son site officiel pour y découvrir tout son univers."
      },
      {
        q: "L'utilisation de Pépite est-elle gratuite ?",
        a: "Oui, l'application est 100% gratuite et sans aucune publicité."
      },
      {
        q: "Dois-je obligatoirement créer un compte pour utiliser l'application ?",
        a: "Non, l'application est accessible sans compte. Cependant, la création d'un compte débloque des fonctionnalités exclusives et très pratiques : l'accès aux notifications (nouveaux drops, baisses de prix, restocks d'articles). Vous pourrez également paramétrer vos préférences."
      }
    ]
  },
  {
    category: "Gestion des Coups de Cœur & Achats",
    questions: [
      {
        q: "Où retrouver tous les vêtements que j'ai \"likés\" ?",
        a: "Tous vos coups de cœur se trouvent dans le menu \"Collections\", sous l'onglet \"Tous les matches\"."
      },
      {
        q: "Puis-je classer ou organiser mes articles favoris ?",
        a: "Bien sûr ! Toujours dans la section \"Collections\", vous pouvez créer des collections personnalisées. Vous pouvez ainsi organiser et regrouper les vêtements qui vous plaisent comme vous le souhaitez, directement depuis n'importe quelle page."
      },
      {
        q: "Comment acheter un vêtement qui me plaît ?",
        a: "Sur la page d'un produit, il vous suffit d'appuyer sur le bouton \"Acheter\". Vous serez alors automatiquement redirigé vers le site internet officiel de la marque pour finaliser votre achat en toute sécurité."
      }
    ]
  },
  {
    category: "Fonctionnalités Avancées",
    questions: [
      {
        q: "Comment accéder à la fonctionnalité qui permet d'être alerté d'un nouveau drop ?",
        a: "Pour recevoir des alertes lors des nouveaux drops (sorties de collections), de baisses de prix ou de restocks, il vous suffit de créer un compte gratuitement sur Pépite et de s'abonner à une marque."
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div
      initial={false}
      className="border-b border-amber-100 dark:border-[hsl(225,10%,16%)] overflow-hidden"
    >
      <button
        className="w-full py-5 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 rounded-lg px-2 group"
        onClick={onClick}
      >
        <span className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="flex-shrink-0 ml-4 text-amber-500"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="pb-5 px-2 text-gray-600 dark:text-gray-400 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function FAQ() {
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center justify-center p-3 bg-amber-100 dark:bg-amber-950/30 rounded-2xl mb-6 text-amber-600 dark:text-amber-400"
        >
          <MessageCircleQuestion className="w-8 h-8" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-amber-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent mb-4"
        >
          Foire Aux Questions
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg text-gray-600 dark:text-gray-400"
        >
          Tout ce que vous devez savoir pour dénicher vos futures pépites.
        </motion.p>
      </div>

      {/* FAQ Sections */}
      <div className="space-y-12">
        {faqData.map((section, sectionIndex) => (
          <motion.div
            key={sectionIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 + sectionIndex * 0.1 }}
            className="bg-white/50 dark:bg-[hsl(225,14%,10%)]/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-sm border border-amber-100 dark:border-[hsl(225,10%,16%)]"
          >
            <h2 className="text-xl font-bold text-amber-600 dark:text-amber-400 mb-6">
              {section.category}
            </h2>
            <div className="flex flex-col">
              {section.questions.map((item, qIndex) => {
                // Calculate an absolute index for the open state management
                const globalIndex = `${sectionIndex}-${qIndex}`;
                return (
                  <FAQItem
                    key={qIndex}
                    question={item.q}
                    answer={item.a}
                    isOpen={openQuestion === globalIndex}
                    onClick={() => toggleQuestion(globalIndex)}
                  />
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Contact Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mt-16 bg-gradient-to-br from-amber-600 to-amber-700 rounded-3xl p-8 md:p-10 text-center text-white shadow-lg relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold mb-4">Vous n'avez pas trouvé votre réponse ?</h2>
          <p className="text-amber-100 mb-8 max-w-xl mx-auto text-lg">
            Notre équipe est là pour vous aider. N'hésitez pas à nous envoyer un message avec votre question.
          </p>
          <a
            href="#/contact"
            className="inline-flex items-center gap-2 bg-white text-stone-900 font-semibold px-8 py-3 rounded-full hover:bg-amber-50 transition-colors shadow-sm hover:shadow-md"
          >
            <MessageCircleQuestion className="w-5 h-5 text-amber-600" />
            Nous contacter
          </a>
        </div>
      </motion.div>
    </div>
  );
}
